import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router'

import CommentList from '@/components/misc/Comments/CommentList'
import EditInvitedCompanyNameButton from '@/components/misc/EditInvitedCompanyNameButton/EditInvitedCompanyNameButton'
import DeleteDirectInviteButton from '@/components/modals/PanelInviteModal/components/DeleteDirectInviteButton'
import { PANEL_INVITE_TAB_KEY } from '@/components/modals/PanelInviteModal/constants'
import {
  PANEL_INVITE_MODAL_SEARCH_PARAMS,
  usePanelInviteModal,
} from '@/components/modals/PanelInviteModal/hooks/usePanelInviteModal'
import { usePanelInviteQuery } from '@/components/modals/PanelInviteModal/hooks/usePanelInviteQuery'
import Overview from '@/components/modals/PanelInviteModal/tabs/Overview/Overview'
import { PanelInviteTab } from '@/components/modals/PanelInviteModal/types'
import { Heading } from '@/components/ui/heading'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { COMMENT_ENTITY_ALIAS, PANEL_INVITE_SOURCE } from '@/lib/constants'

interface Props {
  sourceId: number
  source: string
}

const PanelInvite: React.FC<Props> = ({ sourceId, source }) => {
  const [searchParams] = useSearchParams()
  const { setTab } = usePanelInviteModal()
  const tab =
    searchParams.get(PANEL_INVITE_MODAL_SEARCH_PARAMS.tab) ||
    PANEL_INVITE_TAB_KEY.yourComments
  const panelInviteQuery = usePanelInviteQuery({
    source,
    sourceId,
  })

  const entityAlias =
    source === PANEL_INVITE_SOURCE.DIRECT_INVITE
      ? COMMENT_ENTITY_ALIAS.INVITE_REFERRAL_PARTNER
      : COMMENT_ENTITY_ALIAS.REFERRAL_INVITE

  const panelInvite = panelInviteQuery.data

  const tabs: PanelInviteTab[] = [
    {
      title: 'Invite details',
      key: PANEL_INVITE_TAB_KEY.inviteDetails,
      content: <Overview panelInvite={panelInvite} />,
    },
    {
      title: 'Your Comments',
      key: PANEL_INVITE_TAB_KEY.yourComments,
      content: (
        <CommentList
          entityAlias={entityAlias}
          entityId={panelInvite.sourceId}
        />
      ),
    },
  ]

  return (
    <>
      <Helmet title={panelInvite.companyName} />

      <header className="mb-6 flex flex-wrap gap-4 md:items-center">
        <Heading size={2} className="mb-0">
          {panelInvite.companyName}
        </Heading>

        <EditInvitedCompanyNameButton panelInvite={panelInvite} />

        {panelInvite.source === PANEL_INVITE_SOURCE.DIRECT_INVITE && (
          <DeleteDirectInviteButton
            inviteReferralPartnerId={panelInvite.sourceId}
          />
        )}
      </header>

      <Tabs
        value={tab}
        onValueChange={(tab) => {
          setTab(tab)
        }}
      >
        <TabsList variant="line">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {tabs.map((tab) => (
          <TabsContent key={tab.key} value={tab.key}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </>
  )
}

export default PanelInvite
